import {
  Authority,
  User as graphUser,
  UserType,
  BizsolState,
  ConnectBoardState,
  UserSyncStatus,
} from "@/generated/graphql";
import dayjs from "dayjs";
import ja from "dayjs/locale/ja";

dayjs.locale(ja);

export class User {
  username?: string;
  sub?: string;
  password?: string;
  email?: string;
  companyID?: string;
  companyName?: string;
  userType?: UserType;
  authority?: Authority;
  firstName?: string;
  firstNameKana?: string;
  middleName?: string;
  middleNameKana?: string;
  lastName?: string;
  lastNameKana?: string;
  waitingForChangeToAdmin?: boolean;
  bizsolState?: BizsolState;
  bizsolLoginID?: string;
  connectBoardState?: ConnectBoardState;
  connectBoardLoginID?: string;
  syncStatus?: UserSyncStatus;
  passwordSync?: boolean;
  createdAt?: string;
  updatedAt?: string;

  constructor(user: graphUser) {
    this.username = user.username;
    this.sub = user.sub;
    this.email = user.email;
    this.companyID = user.xCompanyID;
    this.companyName = user.companyName;
    this.userType = user.userType;
    this.authority = user.authority;
    this.firstName = user.firstName;
    this.firstNameKana = user.firstNameKana;
    this.middleName = user.middleName;
    this.middleNameKana = user.middleNameKana;
    this.lastName = user.lastName;
    this.lastNameKana = user.lastNameKana;
    this.waitingForChangeToAdmin = user.waitingForChangeToAdmin;
    this.bizsolState = user.bizsolState;
    this.bizsolLoginID = user.bizsolLoginID;
    this.connectBoardState = user.connectBoardState;
    this.connectBoardLoginID = user.connectBoardLoginID;
    this.syncStatus = user.migrationStatus?.syncStatus ?? undefined;
    this.passwordSync = user.migrationStatus?.passwordSync ?? undefined;
    this.createdAt = user.createdAt;
    this.updatedAt = user.updatedAt;
  }

  fullName(): string {
    let fullName = this.lastName;
    if (this.middleName) {
      fullName += " " + this.middleName;
    }
    fullName += " " + this.firstName;
    return fullName;
  }

  fullNameKana(): string {
    let fullNameKana = this.lastNameKana;
    if (this.middleNameKana) {
      fullNameKana += " " + this.middleNameKana;
    }
    fullNameKana += " " + this.firstNameKana;
    return fullNameKana;
  }

  authorityDisplayName(): AuthorityDisplayName {
    if (this.authority == Authority.Admin) {
      return AuthorityDisplayName.Admin;
    }
    if (this.waitingForChangeToAdmin) {
      return AuthorityDisplayName.AdminWaitingForAuth;
    }
    return AuthorityDisplayName.Member;
  }

  bizsolStateDisplayName(): BizsolStateDisplayName {
    return bizsolStateToDisplayName(this.bizsolState);
  }

  connectBoardStateDisplayName(): ConnectBoardStateDisplayName {
    return connectBoardStateToDisplayName(this.connectBoardState);
  }

  formattedUpdatedAt(): string {
    if (this.updatedAt) {
      const t = dayjs(new Date(this.updatedAt.toString()));
      return t.format("YYYY/MM/DD");
    }
    return "";
  }

  graphUser(): graphUser {
    return {
      sub: this.sub,
      username: this.username,
      authority: this.authority,
      userType: this.userType,
      email: this.email,
      xCompanyID: this.companyID,
      companyName: this.companyName,
      firstName: this.firstName,
      firstNameKana: this.firstNameKana,
      middleName: this.middleName,
      middleNameKana: this.middleNameKana,
      lastName: this.lastName,
      lastNameKana: this.lastNameKana,
      waitingForChangeToAdmin: this.waitingForChangeToAdmin,
      bizsolState: this.bizsolState,
      bizsolLoginID: this.bizsolLoginID,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
    };
  }
}

export enum AuthorityDisplayName {
  Admin = "管理者",
  AdminWaitingForAuth = "一般(管理者メール未認証)",
  Member = "一般",
}

export const AuthorityDisplayNameMap = new Map<Authority, AuthorityDisplayName>(
  [
    [Authority.Admin, AuthorityDisplayName.Admin],
    [Authority.Member, AuthorityDisplayName.Member],
  ]
);

export enum BizsolStateDisplayName {
  Connected = "連携済み",
  Waiting = "連携待ち",
  Unconnected = "未連携",
}

export enum ConnectBoardStateDisplayName {
  Connected = "連携済み",
  Invalid = "無効", // 基本的に発生しない
}

export const bizsolStateToDisplayName = (
  state: BizsolState
): BizsolStateDisplayName => {
  if (state == BizsolState.Master) {
    return BizsolStateDisplayName.Connected;
  }
  if (state == BizsolState.Admin) {
    return BizsolStateDisplayName.Connected;
  }
  if (state == BizsolState.Member) {
    return BizsolStateDisplayName.Connected;
  }
  if (state == BizsolState.Waiting) {
    return BizsolStateDisplayName.Waiting;
  }
  return BizsolStateDisplayName.Unconnected;
};

export const connectBoardStateToDisplayName = (
  state: ConnectBoardState
): ConnectBoardStateDisplayName => {
  if (state == ConnectBoardState.Connected) {
    return ConnectBoardStateDisplayName.Connected;
  }
  return ConnectBoardStateDisplayName.Invalid;
};

export const isConnectedToBizsol = (state: BizsolState): boolean => {
  return [BizsolState.Master, BizsolState.Admin, BizsolState.Member].includes(
    state
  );
};
